import { Link } from "react-router-dom";
import { Gamepad2, ArrowDown, Sparkles } from "lucide-react";

export default function Hero() {
  const scrollToTopics = () => {
    document.getElementById("introduction")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <section
      className="relative overflow-hidden pt-10 pb-24 sm:pt-16"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-jungle-leaf/20 blur-3xl" />
        <div className="absolute right-0 top-32 h-80 w-80 rounded-full bg-tiger-orange/20 blur-3xl" />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-4 py-1.5 font-body text-sm font-bold text-tiger-deep shadow-sticker">
            <Sparkles className="h-4 w-4 text-tiger-orange" />
            A Grade 1 Research Project
          </div>

          <h1 className="mt-5 font-heading text-5xl font-bold leading-[0.95] text-tiger-deep sm:text-6xl lg:text-7xl">
            Welcome to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-tiger-orange">Tigers</span>
              <span className="absolute -bottom-1 left-0 h-3 w-full -rotate-1 rounded-full bg-sun -z-0" />
            </span>
            <span className="text-tiger-deep">forkids</span>
            <span className="text-tiger-orange">.com</span>
          </h1>

          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-tiger-deep sm:text-xl">
            Hi friends! I&apos;m{" "}
            <span className="font-extrabold">Romir Mishra</span> from{" "}
            <span className="font-extrabold">Grade 1</span>, and I LOVE tigers!
            🐯 Come learn 10 super-cool things about them and play my Tiger
            Adventure game!
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/game"
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-3 font-heading text-lg font-bold text-white shadow-stickerLg transition-transform hover:-translate-y-1 hover:shadow-stickerXl"
              data-testid="hero-play-button"
            >
              <Gamepad2 className="h-5 w-5" />
              Play Tiger Game
            </Link>
            <button
              onClick={scrollToTopics}
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-6 py-3 font-heading text-lg font-bold text-tiger-deep shadow-sticker transition-transform hover:-translate-y-1 hover:shadow-stickerLg"
              data-testid="hero-explore-button"
            >
              Start Reading <ArrowDown className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-2 font-body text-sm font-bold text-tiger-deep/80">
            <span className="rounded-full border-[3px] border-tiger-deep bg-jungle-light px-3 py-1">
              🌍 10 Topics
            </span>
            <span className="rounded-full border-[3px] border-tiger-deep bg-tiger-cream px-3 py-1">
              🎮 10 Game Levels
            </span>
            <span className="rounded-full border-[3px] border-tiger-deep bg-white px-3 py-1">
              🏆 Earn Stars & Certificates
            </span>
          </div>
        </div>

        <div
          className="relative mx-auto w-full max-w-md animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          <TigerSticker />
          <div className="absolute -left-6 top-6 -rotate-6 rounded-2xl border-[3px] border-tiger-deep bg-sun px-3 py-1 font-heading font-bold text-tiger-deep shadow-sticker">
            ROAR! 🦁
          </div>
          <div className="absolute -right-2 bottom-12 rotate-6 rounded-2xl border-[3px] border-tiger-deep bg-jungle-leaf px-3 py-1 font-heading font-bold text-white shadow-sticker">
            Save Tigers
          </div>
        </div>
      </div>
    </section>
  );
}

function TigerSticker() {
  return (
    <div className="relative aspect-square w-full rounded-[2rem] border-[4px] border-tiger-deep bg-tiger-cream shadow-stickerXl overflow-hidden">
      {/* Sky gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFE9C0] to-[#FFD089]" />
      {/* Jungle silhouettes */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-jungle" />
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-jungle-dark" />
      {/* Sun */}
      <div className="absolute top-8 right-8 h-20 w-20 rounded-full bg-sun border-[4px] border-tiger-deep shadow-sticker" />
      {/* Tiger SVG */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 h-full w-full animate-bounce-soft"
        aria-hidden
      >
        {/* Body */}
        <ellipse cx="200" cy="270" rx="120" ry="80" fill="#FF8C00" stroke="#281E12" strokeWidth="8" />
        {/* Stripes on body */}
        <path d="M120 240 Q140 270 130 300" stroke="#281E12" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M170 230 Q180 270 165 310" stroke="#281E12" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M230 230 Q240 270 235 310" stroke="#281E12" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M280 240 Q270 270 290 300" stroke="#281E12" strokeWidth="8" strokeLinecap="round" fill="none" />
        {/* Tail */}
        <path d="M310 250 Q360 230 350 180 Q340 160 360 150" stroke="#FF8C00" strokeWidth="22" fill="none" strokeLinecap="round" />
        <path d="M310 250 Q360 230 350 180 Q340 160 360 150" stroke="#281E12" strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.0" />
        {/* Legs */}
        <rect x="130" y="320" width="30" height="50" rx="14" fill="#FF8C00" stroke="#281E12" strokeWidth="6" />
        <rect x="240" y="320" width="30" height="50" rx="14" fill="#FF8C00" stroke="#281E12" strokeWidth="6" />
        {/* Head */}
        <ellipse cx="200" cy="170" rx="95" ry="85" fill="#FF8C00" stroke="#281E12" strokeWidth="8" />
        {/* Ears */}
        <circle cx="135" cy="105" r="26" fill="#FF8C00" stroke="#281E12" strokeWidth="8" />
        <circle cx="265" cy="105" r="26" fill="#FF8C00" stroke="#281E12" strokeWidth="8" />
        <circle cx="135" cy="105" r="12" fill="#FFD9A8" />
        <circle cx="265" cy="105" r="12" fill="#FFD9A8" />
        {/* Face stripes */}
        <path d="M135 100 L120 130" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        <path d="M170 90 L165 130" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        <path d="M230 90 L235 130" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        <path d="M265 100 L280 130" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        <path d="M105 175 L130 175" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        <path d="M270 175 L295 175" stroke="#281E12" strokeWidth="7" strokeLinecap="round" />
        {/* Muzzle */}
        <ellipse cx="200" cy="200" rx="55" ry="38" fill="#FFF6E0" stroke="#281E12" strokeWidth="6" />
        {/* Eyes */}
        <circle cx="170" cy="160" r="14" fill="#281E12" />
        <circle cx="230" cy="160" r="14" fill="#281E12" />
        <circle cx="174" cy="156" r="4" fill="#fff" />
        <circle cx="234" cy="156" r="4" fill="#fff" />
        {/* Nose */}
        <path d="M200 188 L186 204 L214 204 Z" fill="#281E12" />
        {/* Smile */}
        <path d="M200 204 L200 218" stroke="#281E12" strokeWidth="5" strokeLinecap="round" />
        <path d="M200 218 Q183 232 172 218" stroke="#281E12" strokeWidth="5" strokeLinecap="round" fill="none" />
        <path d="M200 218 Q217 232 228 218" stroke="#281E12" strokeWidth="5" strokeLinecap="round" fill="none" />
        {/* Whiskers */}
        <path d="M150 200 L120 195" stroke="#281E12" strokeWidth="3" strokeLinecap="round" />
        <path d="M150 210 L120 215" stroke="#281E12" strokeWidth="3" strokeLinecap="round" />
        <path d="M250 200 L280 195" stroke="#281E12" strokeWidth="3" strokeLinecap="round" />
        <path d="M250 210 L280 215" stroke="#281E12" strokeWidth="3" strokeLinecap="round" />
      </svg>
    </div>
  );
}
